import React from 'react';
import { useServiceWorkerUpdate } from '../../hooks/useServiceWorkerUpdate';

const Spinner: React.FC = () => (
  <svg
    className="text-mt-primary h-4 w-4 shrink-0 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"
    />
  </svg>
);

/**
 * Small, non-blocking "update ready" pill. Mounted once, globally, near the
 * root of the app (see src/App.tsx). Stays invisible until a new build has
 * finished downloading in the background (src/registerServiceWorker.ts);
 * never traps focus or blocks interaction with the rest of the app.
 *
 * By the time this shows, the only thing left to do is reload, so the
 * button does exactly that: Reload hands the page over to the new worker
 * and reloads the instant it takes control (a few hundred milliseconds,
 * with a short fallback so it can never silently do nothing). No download
 * to wait out, so no progress phases.
 */
const UpdateAvailableBanner: React.FC = () => {
  const { updateReady, applying, applyUpdate, dismissUpdate } =
    useServiceWorkerUpdate();

  if (!updateReady) return null;

  // Native `disabled` would blur a focused button to <body> the instant
  // React applies the attribute, which is not acceptable for a button the
  // visitor just deliberately pressed: use `aria-disabled` (announced as
  // disabled, still a real focusable element) and no-op the handler, so the
  // SAME button stays mounted and focused until the page reloads.
  const handleReloadClick = () => {
    if (applying) return;
    applyUpdate();
  };

  return (
    <div
      role="status"
      className="bg-mt-surface fixed inset-x-4 bottom-4 z-1000 mx-auto flex max-w-sm items-center gap-3 rounded-lg px-4 py-3 text-left shadow-2xl ring-1 ring-black/5 sm:inset-x-auto sm:right-4"
    >
      {applying ? <Spinner /> : null}
      <p className="text-mt-ink-soft flex-1 text-sm">
        {applying ? 'Reloading…' : 'An update is ready.'}
      </p>
      <button
        type="button"
        onClick={handleReloadClick}
        aria-disabled={applying}
        aria-busy={applying}
        className={`mt-btn bg-mt-primary text-mt-on-primary focus-visible:outline-mt-primary inline-flex shrink-0 items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 ${
          applying
            ? 'cursor-not-allowed opacity-70'
            : 'hover:bg-mt-primary-hover'
        }`}
      >
        {applying ? 'Reloading…' : 'Reload'}
      </button>
      {/* A reload is already on its way once pressed, so offering Dismiss
          then would be misleading. */}
      {!applying ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismissUpdate}
          // 44px hit area (negative margins keep the pill the same height)
          // and ink-muted rather than ink-faint: both from the September
          // accessibility review's R2.
          className="text-mt-ink-muted hover:text-mt-ink-soft focus-visible:outline-mt-primary -my-2 -mr-2 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      ) : null}
    </div>
  );
};

export default UpdateAvailableBanner;
